import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { readingPlanService } from './services/readingPlan.service';
import { userService } from './services/user.service';
import { isDayCompleted } from './services/user.utils';
import {
  getDayOfYear,
  getNextDayOfYear,
  getPreviousDayOfYear,
} from './utils/planUtils';
import { BibleVersion, isValidBibleVersion } from './models';
import DailyCard from './components/DailyCard';
import Signup from './components/Signup';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import WelcomeMessage from './components/WelcomeMessage';
import DateNavigation from './components/DateNavigation';
import Settings from './components/Settings';
import VersionSelect from './components/VersionSelect';
import { Skeleton, IconButton, Tooltip, Button } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import MenuComponent from './components/MenuComponent';
import ContactView from './pages/ContactView';
import packageJson from '../package.json';
import {
  removeTodayReadingIntent,
  shouldOpenTodaysReading,
} from './utils/notificationNavigation';

const App: React.FC = () => {
  const [view, setView] = useState<
    'signup' | 'login' | 'daily' | 'forgot' | 'settings' | 'contacts'
  >('login');
  const [user, setUser] = useState<User | null>(null);
  const [userName, setUserName] = useState<string>(''); // Store user's name
  const [isAdmin, setIsAdmin] = useState<boolean>(false); // Store user's admin status
  const [isLoading, setIsLoading] = useState(true);
  const [readings, setReadings] = useState<string[]>([]);
  const [bibleVersion, setBibleVersion] = useState<BibleVersion>('ESV'); // Default to ESV
  const [fontSize, setFontSize] = useState<string>('md'); // Default to md
  const [currentDayOfYear, setCurrentDayOfYear] = useState(
    getDayOfYear(new Date())
  );
  const [isRead, setIsRead] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);

  // Check if current date is today
  const today = new Date();
  const todayDayOfYear = getDayOfYear(today);
  const isTodaySelected = currentDayOfYear === todayDayOfYear;

  const goToToday = () => {
    setCurrentDayOfYear(todayDayOfYear);
  };

  useEffect(() => {
    if (!shouldOpenTodaysReading(window.location.search)) return;

    setCurrentDayOfYear(getDayOfYear(new Date()));
    setView('daily');
    window.history.replaceState(
      {},
      document.title,
      removeTodayReadingIntent(new URL(window.location.href))
    );
  }, []);

  const handleDismissWelcome = () => {
    setShowWelcomeMessage(false);
  };

  const auth = getAuth();

  useEffect(() => {
    const fetchReadings = async () => {
      try {
        const readingEntry = await readingPlanService.getReadingsForDay(
          currentDayOfYear
        );
        if (readingEntry) {
          setReadings(readingEntry.readings);
        } else {
          setReadings([]);
        }
      } catch (error) {
        console.error('Error fetching readings:', error);
        setReadings([]);
      }
    };

    fetchReadings();
  }, [currentDayOfYear]);

  const currentDayKey = `2026-day-${currentDayOfYear}`;

  useEffect(() => {
    if (user) {
      const fetchReadStatus = async () => {
        try {
          // Parse year and day from currentDayKey (format: "2026-day-150")
          const match = currentDayKey.match(/^(\d{4})-day-(\d+)$/);
          if (!match) {
            setIsRead(false);
            return;
          }

          const year = match[1];
          const dayNumber = parseInt(match[2], 10);

          const isReadToday = await isDayCompleted(user.uid, dayNumber, year);
          setIsRead(isReadToday);
        } catch (error) {
          console.error('Error fetching read status:', error);
          setIsRead(false);
        }
      };

      fetchReadStatus();
    }
  }, [currentDayOfYear, user, currentDayKey]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setView('daily');

        // Fetch user profile data from Firestore
        try {
          const userData = await userService.getUser(currentUser.uid);
          if (userData) {
            setUserName(userData.firstName || '');
            setIsAdmin(userData.isAdmin || false);
            setBibleVersion(
              userData.defaultBibleVersion &&
                isValidBibleVersion(userData.defaultBibleVersion)
                ? userData.defaultBibleVersion
                : 'ESV'
            );
            setFontSize(userData.defaultFontSize || 'md');
          } else {
            // Initialize user if they don't have a profile yet
            await userService.createOrUpdateUser(currentUser.uid, {
              firstName: '',
              isAdmin: false,
              defaultBibleVersion: 'ESV',
              defaultFontSize: 'md',
              completedDays: {},
            });
            setUserName('');
            setIsAdmin(false);
            setBibleVersion('ESV');
            setFontSize('md');
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          // Set defaults if there's an error
          setUserName('');
          setIsAdmin(false);
          setBibleVersion('ESV');
          setFontSize('md');
        }
      } else {
        setUser(null);
        setView('login');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const appVersion = packageJson.version;

  const handleNavigation = (
    newView: 'signup' | 'login' | 'daily' | 'forgot' | 'settings'
  ) => {
    setView(newView);
  };

  const handleBibleVersionChange = (version: string) => {
    if (isValidBibleVersion(version)) {
      setBibleVersion(version);
    } else {
      console.error(`Invalid Bible version: ${version}`);
    }
  };

  const handleFontSizeChange = (size: string) => {
    setFontSize(size);
  };

  const navigateDay = (direction: number) => {
    const newDayOfYear =
      direction > 0
        ? getNextDayOfYear(currentDayOfYear)
        : getPreviousDayOfYear(currentDayOfYear);
    setCurrentDayOfYear(newDayOfYear);
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen bg-gradient-to-r from-blue-500 to-blue-700'>
        <div className='text-center'>
          <Skeleton
            variant='circular'
            width={80}
            height={80}
            style={{ margin: '0 auto' }}
          />
          <Skeleton
            variant='text'
            width={210}
            height={40}
            style={{ margin: '20px auto' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <MenuComponent user={user} handleNavigation={handleNavigation} />
        {view === 'signup' && <Signup />}
        {view === 'login' && (
          <Login
            onLoginSuccess={() => handleNavigation('daily')}
            onSignUpClick={() => setView('signup')}
            onForgotPasswordClick={() => setView('forgot')}
          />
        )}
        {view === 'forgot' && (
          <ForgotPassword onResetComplete={() => handleNavigation('login')} />
        )}
        {view === 'settings' && (
          <Settings
            user={user}
            onBibleVersionChange={handleBibleVersionChange}
            onFontSizeChange={handleFontSizeChange}
          />
        )}
        {view === 'contacts' && <ContactView />}
        {view === 'daily' && (
          <div className='container mx-auto p-4'>
            {/* Compact Header with Navigation */}
            <div className='flex items-center justify-between gap-4 mb-4'>
              <Tooltip title='Previous Day'>
                <div>
                  <IconButton
                    color='primary'
                    onClick={() => navigateDay(-1)}
                    disabled={currentDayOfYear <= 1}
                    size='small'
                    sx={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#9ca3af',
                        color: '#6b7280',
                      },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                </div>
              </Tooltip>

              <div className='text-center'>
                <h1 className='text-2xl font-bold mb-1'>Daily Bible Reading</h1>
              </div>

              <Tooltip title='Next Day'>
                <div>
                  <IconButton
                    color='primary'
                    onClick={() => navigateDay(1)}
                    disabled={currentDayOfYear >= 365}
                    size='small'
                    sx={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#2563eb',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#9ca3af',
                        color: '#6b7280',
                      },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </div>
              </Tooltip>
            </div>
            {user && showWelcomeMessage && (
              <WelcomeMessage
                userName={userName}
                isRead={isRead}
                onDismiss={handleDismissWelcome}
              />
            )}
            <div className='flex items-center justify-between mb-2'>
              {/* Compact Date Navigation */}
              <DateNavigation
                currentDayOfYear={currentDayOfYear}
                onDayOfYearChange={setCurrentDayOfYear}
              />

              {/* Go to Today button - inline on larger screens */}
              {!isTodaySelected && (
                <div className='hidden md:block'>
                  <Button
                    variant='contained'
                    color='success'
                    size='small'
                    onClick={goToToday}
                    sx={{
                      fontSize: '0.875rem',
                      padding: '4px 12px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Go to Today
                  </Button>
                </div>
              )}

              <VersionSelect
                onSelect={handleBibleVersionChange}
                defaultVersion={bibleVersion}
              />
            </div>

            {/* Go to Today button - below on smaller screens */}
            {!isTodaySelected && (
              <div className='flex justify-center mb-4 md:hidden'>
                <Button
                  variant='contained'
                  color='success'
                  size='small'
                  onClick={goToToday}
                  sx={{
                    fontSize: '0.875rem',
                    padding: '4px 12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Go to Today
                </Button>
              </div>
            )}

            <DailyCard
              dayOfYear={currentDayOfYear}
              readings={readings}
              version={bibleVersion}
              fontSize={fontSize as 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'}
              setIsRead={setIsRead}
              isRead={isRead}
              currentDayKey={currentDayKey}
            />
          </div>
        )}
      </div>
      <footer className='w-full text-center bg-gray-200 py-2'>
        {/*Only show this button if the user is an admin  */}
        {isAdmin && (
          <button
            onClick={() => setView('contacts')}
            className='text-blue-500 underline'
          >
            Contacts
          </button>
        )}
        <p className='text-sm text-gray-600'>App Version: {appVersion}</p>
      </footer>
    </div>
  );
};

export default App;
